import { FC, useRef, useState } from "react";
import useDataInitializer from "../../hooks/useDataInitializer";
import { useDebounce } from "../../hooks/useDebounce";
import useListFilters from "../../hooks/useListFilters";
import { useI18n } from "../../hooks/useTranslations";
import { useSuppliersApi } from "../../http/suppliers/api";
import { Supplier } from "../../http/suppliers/types";
import { PaginationQueryWithFields } from "../../types";
import { isWithinLast48h } from "../../types/utils";
import Header from "../ui/Header";
import NewItemIcon from "../ui/NewItemIcon";
import Panel from "../ui/Panel";

import { useNavigate } from "react-router-dom";
import { useUserSettings } from "../../context/userSettingsContext";
import { Action, getDeleteAction, getEditAction, getHistoryAction } from "../ui/ListItemMenu";
import Table from "../ui/Table";
import CreateSupplierButton from "./CreateSupplierButton";
import DeleteSupplierDialog from "./DeleteSupplierDialog";
import SupplierHistoryDialog from "./SupplierHistoryDialog";

const SUPPLIER_FIELDS = ["id", "createdAt", "name", "address", "email", "phoneNumber", "versionNumber"] as const;
type SupplierField = (typeof SUPPLIER_FIELDS)[number];
type SupplierSearch = Pick<Supplier, SupplierField>;

const Suppliers: FC = () => {
  const i18n = useI18n();
  const userSettings = useUserSettings();
  const suppliersApi = useSuppliersApi();
  const filters = useListFilters({ isSearchable: true });
  const debouncedValue = useDebounce(filters.state.q);
  const navigate = useNavigate();
  const aborter = useRef(new AbortController());
  const [itemToDelete, setItemToDelete] = useState<SupplierSearch>();
  const [itemToShowVersions, setItemToShowVersions] = useState<SupplierSearch>();

  const query: PaginationQueryWithFields<SupplierSearch> = {
    ...filters.state,
    q: debouncedValue,
    fields: SUPPLIER_FIELDS,
  };

  const result = suppliersApi.useAdminSuppliers(query, aborter.current.signal);
  const [data] = useDataInitializer(result.data, result.error);

  const orderOptions = {
    by: filters.state.orderBy,
    desc: filters.state.desc,
    setBy: filters.setOrder,
  };

  const getRowActions = (item: SupplierSearch): Action[] => {
    const actions: Action[] = [];

    if (userSettings.permissions.suppliers.update) {
      actions.push(getEditAction(i18n, () => navigate("/admin/suppliers/" + item.id)));
    }

    actions.push(getHistoryAction(i18n, () => setItemToShowVersions(item)));

    if (userSettings.permissions.suppliers.delete) {
      actions.push(getDeleteAction(i18n, () => setItemToDelete(item), true));
    }

    return actions;
  };

  return (
    <>
      <Header title={i18n.t.suppliers.plural} />
      <Panel>
        <Table<SupplierSearch>
          filters={filters}
          searchOptions={{
            onChange: () => {
              aborter.current.abort();
              aborter.current = new AbortController();
            },
          }}
          buttons={{ main: userSettings.permissions.suppliers.insert && <CreateSupplierButton /> }}
          head={
            <>
              <Table.Th title={i18n.t.common.name} fieldName="supplier.name" order={orderOptions} />
              <Table.Th title={i18n.t.common.address} fieldName="supplier.address" order={orderOptions} />
              <Table.Th title={i18n.t.common.email} fieldName="supplier.email" order={orderOptions} />
              <Table.Th title={i18n.t.common.phoneNumber} fieldName="supplier.phoneNumber" order={orderOptions} />
              <th></th>
            </>
          }
          onRowClick={(item) => {
            navigate("/admin/suppliers/" + item.id);
          }}
          renderRow={(item) => (
            <>
              <td>
                {isWithinLast48h(item.createdAt) && <NewItemIcon />}
                {item.name}
              </td>
              <td>{item.address}</td>
              <td>{item.email}</td>
              <td>{item.phoneNumber}</td>
            </>
          )}
          rowActions={getRowActions}
          error={result.error}
          items={data?.items}
          total={data?.total}
        />
      </Panel>

      {!!itemToDelete?.id && (
        <DeleteSupplierDialog supplierId={itemToDelete.id} onClose={() => setItemToDelete(undefined)} />
      )}

      {!!itemToShowVersions && (
        <SupplierHistoryDialog
          itemToShowVersionsId={itemToShowVersions.id}
          itemVersionToShowVersions={itemToShowVersions.versionNumber}
          onClose={() => setItemToShowVersions(undefined)}
        />
      )}
    </>
  );
};

export default Suppliers;
